"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, CircleQuestionMark as HelpCircle, PhoneCall, Wrench, TriangleAlert as AlertTriangle, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqList = [{
  id: "item-1",
  question: "What causes a gas water heater to stop heating?",
  answer: "Common causes include an extinguished pilot light, a malfunctioning thermocouple, a faulty gas control valve, or an interrupted gas supply line. Mineral sediment buildup around the burner can also insulate the heating area and disrupt heat transfer.",
  linkText: "Explore Water Heater Repair Services",
  linkUrl: "/water-heater-repair/"
}, {
  id: "item-2",
  question: "Is a gas water heater leak dangerous?",
  answer: "Yes, leaks can be dangerous depending on the source. A gas supply leak presents an immediate fire and carbon monoxide hazard, requiring immediate shutoff and evacuation. A water leak from the tank itself can cause structural floor damage, electrical shorts, or mold growth, indicating potential internal tank corrosion.",
  linkText: "Request Emergency Plumbing Help",
  linkUrl: "/emergency-plumbing/"
}, {
  id: "item-3",
  question: "Why is my gas water heater making noise?",
  answer: "Popping, rumbling, or boiling noises usually indicate sediment accumulation at the bottom of the tank. As the burner heats the metal underneath, trapped water under sediment layers turns into steam pockets and pops. A high-pitched whistling noise often points to high pressure relief or a restricted valve.",
  linkText: "Diagnose Water Heater Problems",
  linkUrl: "/water-heater-problems/"
}, {
  id: "item-4",
  question: "How often should a gas water heater be serviced?",
  answer: "A standard tank gas water heater should be inspected and flushed at least once a year. Annual maintenance includes flushing sediment from the tank bottom, checking the sacrificial anode rod for corrosion, testing the temperature and pressure (T&P) relief valve, and inspecting the burner assembly.",
  linkText: "View Water Heater Maintenance Options",
  linkUrl: "/water-heater/"
}, {
  id: "item-5",
  question: "When should I replace vs. repair a gas water heater?",
  answer: "Repair is typically recommended for minor component issues (like a thermocouple, igniter, or valve) on units under 8 to 10 years old. Replacement is necessary when the tank body itself is leaking, when repair costs exceed half the value of a new unit, or when units surpass 10 to 12 years of age.",
  linkText: "Learn About Water Heater Replacement",
  linkUrl: "/water-heater-replacement/"
}, {
  id: "item-6",
  question: "What are the benefits of a tankless gas water heater?",
  answer: "Tankless gas water heaters provide continuous hot water on demand without holding a 40-to-50 gallon standby reservoir. They take up significantly less wall space, reduce standby energy loss, and generally have a longer lifespan (15 to 20 years with regular descaling) compared to storage tank heaters.",
  linkText: "See Tankless Gas Water Heater Services",
  linkUrl: "/tankless-water-heater-service/"
}];
export default function WaterHeaterFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="gaswaterheaterfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-5xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-2xl mx-auto mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-semibold text-xs tracking-normal">
              <Flame className="size-3.5 text-accent mr-1" />
              Gas Water Heater FAQs
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-default tracking-tight text-foreground">
            Clear Answers to Common Gas Water Heater Questions
          </h2>
          <p className="mt-3 text-muted-foreground text-base md:text-lg">
            Practical facts on performance, safety standards, routine maintenance, and replacement timelines for Jonesboro homeowners.
          </p>
        </motion.div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="bg-card text-card-foreground border border-border rounded-xl p-6 md:p-8 shadow-sm">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            {faqList.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-border">
                <AccordionTrigger className="text-left font-default font-semibold text-foreground hover:text-primary py-4 text-base md:text-lg hover:no-underline">
                  <div className="flex items-start gap-3">
                    <span className="flex size-7 items-center justify-center rounded-md bg-secondary text-secondary-foreground text-xs font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span>{item.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pl-10 pt-1 pb-4">
                  <p>{item.answer}</p>
                  {item.linkUrl && <div className="mt-3 pt-2">
                      <Link to={item.linkUrl} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                        {item.linkText}
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </div>}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start gap-4 p-5 rounded-lg border border-border bg-muted/40">
            <div className="rounded-lg bg-primary/10 p-2.5 text-primary shrink-0">
              <AlertTriangle className="size-5" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-base">Smell gas near your unit?</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Shut off the gas supply line immediately, avoid operating switches, step outside, and call emergency services or a qualified plumbing technician.
              </p>
              <div className="mt-3">
                <Button asChild size="sm" variant="destructive">
                  <Link to="/emergency-plumbing/">
                    <PhoneCall className="size-3.5" />
                    Emergency Support
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4 p-5 rounded-lg border border-border bg-muted/40">
            <div className="rounded-lg bg-accent/20 p-2.5 text-accent-foreground shrink-0">
              <Wrench className="size-5 text-accent" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-base">Need a diagnostic or tune-up?</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Schedule a complete burner, thermocouple, and tank inspection to maintain heating performance and energy efficiency.
              </p>
              <div className="mt-3">
                <Button asChild size="sm" variant="outline">
                  <Link to="/contact/">
                    <ShieldCheck className="size-3.5" />
                    Book Service Call
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}